// src/Frameworks/DialogHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./styles/mindmap.css";

const API = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
const LS_ACTIVE_SESSION_ID = "vybly.activeSessionId";
const LS_TURNS_PREFIX = "vybly.turns.";
const LS_LAST_USER = "vybly.lastUserText";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

function cacheKey(sessionId) {
  return LS_TURNS_PREFIX + sessionId;
}
function readCache(sessionId) {
  try {
    const raw = localStorage.getItem(cacheKey(sessionId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeCache(sessionId, turns) {
  try {
    localStorage.setItem(cacheKey(sessionId), JSON.stringify(turns));
  } catch {}
}

function pick(val, ...alts) {
  if (val !== undefined && val !== null) return val;
  for (const a of alts) if (a !== undefined && a !== null) return a;
  return undefined;
}

export default function DialogHistory({
  sessionId,
  latestExchange,  // { user_text?, assistant_text? } (best-effort)
  latestAssistantBody, // payload.body (authoritative assistant reply)
  turnBump = 0,        // increments each turn (graph.turnCount or turns.length)
}) {
  const sid =
    sessionId || localStorage.getItem(LS_ACTIVE_SESSION_ID) || null;

  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState([]); // newest-first array of {role,text,turn_number?,created_at?}

  // Pull the full, authoritative history when a session id exists (saved or floating)
  useEffect(() => {
    if (!sid) {
      setTurns([]);
      return;
    }
    let cancelled = false;

    (async function load() {
      setLoading(true);
      try {
        const r = await fetch(
          `${API}/frameworks/mindmap/session/${sid}/turns?limit=200`,
          { headers: { "Content-Type": "application/json", ...authHeaders() }, credentials: "include" }
        );
        if (!r.ok) {
          // fall back to cache if server not ready
          const cached = readCache(sid);
          if (!cancelled) setTurns(cached);
          return;
        }
        const data = await r.json();

        // Normalize DB rows → newest-first flattened messages
        // Keep per-turn pairing and allow empty strings (don’t trim away content)
        const flat = [];
        const rows = Array.isArray(data.turns) ? data.turns : [];
        for (const t of rows) {
          // NOTE: rows are newest (highest turn_number) → oldest
          const u = t.user_text;
          const a = t.assistant_text;
          if (typeof a === "string") {
            flat.unshift({ role: "assistant", text: a, turn_number: t.turn_number, created_at: t.created_at });
          }
          if (typeof u === "string") {
            flat.unshift({ role: "user", text: u, turn_number: t.turn_number, created_at: t.created_at });
          }
        }
        // Now flat is oldest→newest; flip to newest→oldest for UI
        flat.reverse();

        // Persist and set
        if (!cancelled) {
          writeCache(sid, flat);
          setTurns(flat);
        }
      } catch {
        const cached = readCache(sid);
        if (!cancelled) setTurns(cached);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [sid]);

  // Merge the most recent “in-flight” pair for floating sessions (and as a guard for saved)
  useEffect(() => {
    if (!sid) return;

    const cached = readCache(sid);
    const next = [...cached];

    // Assistant: prefer explicit prop (payload.body), otherwise from latestExchange
    const assistant_text = pick(
      latestAssistantBody,
      latestExchange?.assistant_text,
      latestExchange?.assistantText
    );
    // User: prefer last submitted (persisted at submit), fallback to latestExchange variants
    const lastUserLocal = localStorage.getItem(LS_LAST_USER) || "";
    const user_text = pick(
      lastUserLocal,
      latestExchange?.user_text,
      latestExchange?.userText,
      latestExchange?.text
    );

    // If nothing to merge, bail
    if (!assistant_text && !user_text) {
      // Still reflect any server/cached updates when turnBump changes
      setTurns(next);
      return;
    }

    // Deduplicate simple top-of-stack equality to avoid double inserts
    const t0 = next[0]?.text ?? "";
    if (user_text && user_text !== t0) {
    next.unshift({ role: "user", text: user_text });
    }
    const t1 = next[0]?.text ?? "";
    if (assistant_text && assistant_text !== t1) {
    next.unshift({ role: "assistant", text: assistant_text });
    }

    writeCache(sid, next);
    setTurns(next);
    // Re-run whenever a new turn completes / props change
  }, [
    sid,
    turnBump,
    latestAssistantBody,
    latestExchange?.assistant_text,
    latestExchange?.assistantText,
    latestExchange?.user_text,
    latestExchange?.userText,
    latestExchange?.text,
  ]);

  // Fill the very-first user message on saved sessions if DB omitted it:
  useEffect(() => {
    if (!sid || !turns.length) return;

    // If the oldest pair begins with assistant and there’s no user beneath,
    // use the earliest user we ever cached for this session as a fallback.
    const newestToOldest = turns;
    const oldestToNewest = [...newestToOldest].reverse();
    const first = oldestToNewest[0];
    const second = oldestToNewest[1];

    const looksLikeFirstAssistantOnly =
      first?.role === "assistant" && (!second || second.role !== "user");

    if (!looksLikeFirstAssistantOnly) return;

    // Find earliest user we’ve cached for this session (from submit-time persist)
    const earliestUser = oldestToNewest.find(m => m.role === "user");
    if (!earliestUser) return;

    const patchedOldest = [earliestUser, ...oldestToNewest];
    const fixed = patchedOldest.reverse(); // back to newest→oldest
    writeCache(sid, fixed);
    setTurns(fixed);
  }, [sid, turns]);

  const body = useMemo(() => {
    if (collapsed) return null;
    if (loading) return <div className="tiny-loading">Loading…</div>;
    if (!turns.length) return <div className="empty">No dialog yet.</div>;

    return (
      <div className="dialog-history-messages" aria-live="polite">
        {turns.map((m, i) => (
          <div key={`${m.role}-${i}`} className={`dialog-message ${m.role}`}>
            {m.text}
          </div>
        ))}
      </div>
    );
  }, [collapsed, loading, turns]);

  return (
    <section className="dialog-history-card">
      <div className="dialog-history-header">
        <div className="title">Dialog History</div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls="dialog-history-body"
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>
      <div id="dialog-history-body">{body}</div>
    </section>
  );
}
